import React, { useRef, useState } from 'react';
import './IntertownTrip.css';
import MapRecenterIcon from './MapRecenterIcon';
import {
  IconAlert, IconBack, IconCheck, IconClock, IconFlag, IconHeadset, IconInfo, IconPhone, IconShare, IconShield,
} from './IntertownIcons';
import { formatRupees } from '../utils/intertownDisplay';
import { formatDriverName } from '../utils/driverName';
import { arrivalClock, clockDuration, etaHeadline, pickupSteps, timestampMs } from '../utils/intertownTrip';

/**
 * The Intertown trip, from "finding an Anna" to the end of a return trip, drawn the way the
 * big ride apps draw it: the map is the screen and one sheet below it holds what matters now.
 * Purely presentational: the container owns the live location, the clock and every action, so
 * each state can be drawn (and tested) without a login or a network.
 */

const T = (t, key, vars) => t(`intertown.trip.${key}`, vars);

function Avatar({ name, src }) {
  const [failed, setFailed] = useState(false);
  const initial = String(name || 'A').replace(/^(Anna|Captain)\s+/i, '').trim().charAt(0).toUpperCase() || 'A';
  return (
    <span className="itt-avatar" aria-hidden="true">
      {src && !failed ? <img src={src} alt="" onError={() => setFailed(true)} /> : initial}
    </span>
  );
}

function Steps({ t, phase }) {
  const steps = pickupSteps(phase);
  if (!steps.length) return null;
  const label = { finding: 'stepFinding', to_pickup: 'stepOnTheWay', at_pickup: 'stepAtPickup' };
  return (
    <ol className="itt-steps" aria-label={t('active.rideProgress')}>
      {steps.map((step) => (
        <li key={step.key} className={`itt-step is-${step.state}`} aria-current={step.state === 'current' ? 'step' : undefined}>
          <span className="itt-step-dot" aria-hidden="true">{step.state === 'done' ? <IconCheck size={11} /> : null}</span>
          <span className="itt-step-label">{T(t, label[step.key])}</span>
        </li>
      ))}
    </ol>
  );
}

function PinCard({ t, otp }) {
  const digits = String(otp || '').replace(/\D/g, '').slice(0, 4).split('');
  return (
    <div className="itt-pin" role="group" aria-label={T(t, 'pinLabel')}>
      <span className="itt-pin-label">{T(t, 'pinLabel')}</span>
      <div className="itt-pin-digits">
        {[0, 1, 2, 3].map((i) => <span key={i} className="itt-pin-digit">{digits[i] || '–'}</span>)}
      </div>
    </div>
  );
}

function DriverRow({ t, trip, driverProfile, compact, onCall, onShowDriver }) {
  const rawName = trip.driverName || driverProfile?.name || '';
  const name = formatDriverName(rawName);
  const plate = trip.vehicleNumber || driverProfile?.vehicleNumber || '';
  const model = trip.vehicleModel || driverProfile?.vehicleModel || T(t, 'vehicleAuto');
  const photo = driverProfile?.photoUrl || trip.driverPhoto || '';
  return (
    <div className={`itt-driver${compact ? ' is-compact' : ''}`}>
      <Avatar name={rawName} src={photo} />
      <div className="itt-driver-main">
        <strong className="itt-driver-name">
          <span>{name}</span>
          <span className="itt-verified" role="img" aria-label={t('intertown.chipVerifiedAnna')}><IconCheck size={13} /></span>
        </strong>
        <span className="itt-driver-sub">{[plate, model].filter(Boolean).join(' · ')}</span>
        {!compact && (
          <button type="button" className="itt-link" onClick={onShowDriver}>{T(t, 'annaDetails')} ›</button>
        )}
      </div>
      <button type="button" className="itt-call" onClick={onCall} aria-label={`${T(t, 'call')} ${name}`}>
        <span className="itt-call-ico"><IconPhone size={20} /></span>
        <span className="itt-call-label">{T(t, 'call')}</span>
      </button>
    </div>
  );
}

function FareRows({ t, trip, phase, waiting }) {
  const total = Number(trip.totalFare) || Number(trip.baseFare) || 0;
  const base = Number(trip.baseFare) || total;
  if (phase === 'waiting' || phase === 'returning') {
    const final = phase === 'returning';
    const charge = final ? (Number(trip.waitingCharge) || 0) : waiting.charge;
    const chargeText = !final && !waiting.inFreeTime
      ? T(t, 'soFar', { amount: formatRupees(charge) })
      : formatRupees(charge);
    return (
      <div className="itt-fare">
        <div className="itt-fare-row"><span>{T(t, 'baseFare')}</span><b>{formatRupees(base)}</b></div>
        <div className="itt-fare-row"><span>{T(t, 'waitingCharge')}</span><b>{chargeText}</b></div>
        {final && <div className="itt-fare-row is-total"><span>{T(t, 'tripFare')}</span><b>{formatRupees(total)}</b></div>}
      </div>
    );
  }
  return (
    <div className="itt-fare">
      <div className="itt-fare-row is-total">
        <span>
          {T(t, 'tripFare')}
          <small>{t(trip.tripType === 'return' ? 'intertown.returnTrip' : 'intertown.oneWay')}</small>
        </span>
        <b>{formatRupees(total)}</b>
      </div>
    </div>
  );
}

function WaitCard({ t, policy, waiting }) {
  if (!policy) return null;
  const rule = T(t, policy.unit === 'hour' ? 'waitThenHour' : 'waitThenMinute', { rate: formatRupees(policy.rate) });
  return (
    <div className="itt-wait">
      <span className="itt-wait-ico"><IconClock size={22} /></span>
      <div>
        <strong>{T(t, 'waitFree', { n: waiting.freeMinutes })}</strong>
        <span>{rule}</span>
      </div>
    </div>
  );
}

function Actions({ t, onShare, onOpenSafety, hint = true }) {
  return (
    <>
      <div className="itt-actions">
        <button type="button" className="itt-action" onClick={onShare}><IconShare size={18} />{T(t, 'shareDetails')}</button>
        <button type="button" className="itt-action" onClick={onOpenSafety}><IconShield size={18} />{T(t, 'safetyHelp')}</button>
      </div>
      {hint && <p className="itt-hint">{T(t, 'shareHint')}</p>}
    </>
  );
}

// "Live" tolerates a few missed beats (a parked phone reports slowly), so a fix a minute or two
// old still counts as live: say how old it is instead of calling it "just now".
function LiveLine({ t, freshness, lastUpdateMin }) {
  const aging = freshness === 'live' && lastUpdateMin !== null && lastUpdateMin >= 1;
  const text = {
    live: aging ? T(t, 'lastUpdate', { min: lastUpdateMin }) : T(t, 'liveNow'),
    stale: T(t, 'liveStale'),
    waiting: t('intertown.mapWaitingLocation'),
  }[freshness];
  return <p className={`itt-live is-${freshness}`}><i aria-hidden="true" />{text}</p>;
}

function SafetySheet({
  t, supportAvailable, alerting, alerted, onClose, onCall112, onAlert, onCallSupport, onReport,
}) {
  const rows = [
    { key: 'call112', icon: <IconPhone size={22} />, tone: 'danger', onClick: onCall112 },
    // The server confirms MyTownly's alert. Family sharing still needs the rider to press Send.
    {
      key: 'alertTeam', icon: <IconAlert size={22} />, tone: 'danger', onClick: onAlert, disabled: alerting, title: alerted ? 'alertTeamDone' : 'alertTeam',
    },
    ...(supportAvailable ? [{ key: 'callSupport', icon: <IconHeadset size={22} />, tone: 'good', onClick: onCallSupport }] : []),
    { key: 'reportIssue', icon: <IconFlag size={22} />, tone: 'danger', onClick: onReport },
  ];
  return (
    <div className="itt-overlay" role="presentation" onClick={onClose}>
      <div className="itt-safety" role="dialog" aria-modal="true" aria-label={T(t, 'safetyTitle')} onClick={(event) => event.stopPropagation()}>
        <span className="itt-handle-static" aria-hidden="true" />
        <h2>{T(t, 'safetyTitle')}</h2>
        {rows.map((row) => (
          <button key={row.key} type="button" className={`itt-safe-row is-${row.tone}`} onClick={row.onClick} disabled={row.disabled}>
            <span className="itt-safe-ico">{row.icon}</span>
            <span className="itt-safe-copy"><b>{T(t, row.title || row.key)}</b><small>{T(t, row.key === 'alertTeam' && alerted ? 'alertTeamDoneSub' : `${row.key}Sub`)}</small></span>
          </button>
        ))}
        <button type="button" className="itt-safe-close" onClick={onClose}>{t('common.close')}</button>
      </div>
    </div>
  );
}

export default function IntertownTripView({
  t, lang = 'en', trip, driverProfile, tripOtp, phase, chip, routeText, pickupName, destinationName,
  eta = null, nowMs, freshness = 'waiting', lastUpdateMin = null, delayed = false,
  waiting = { waitedMinutes: 0, minutesStarted: 0, freeMinutes: 0, charge: 0, inFreeTime: true, elapsedMs: 0 },
  canCancel = false, minimized = false, sheetRef, sheetHeight, safetyOpen = false, supportAvailable = false,
  alerting = false, alerted = false, showBack = false, panned = false, map = null,
  onToggleMinimized, onCancel, onCall, onShowDriver, onShare, onOpenSafety, onCloseSafety,
  onCall112, onAlert, onCallSupport, onReport, onBack, onRecenter,
}) {
  const sheetGesture = useRef(null);
  const suppressHandleClick = useRef(false);
  const startSheetGesture = (event) => {
    if (!event.isPrimary || event.button !== 0) return;
    suppressHandleClick.current = false;
    sheetGesture.current = { id: event.pointerId, x: event.clientX, y: event.clientY, minimized };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const finishSheetGesture = (event) => {
    const gesture = sheetGesture.current;
    if (!gesture || gesture.id !== event.pointerId) return;
    sheetGesture.current = null;
    const dy = event.clientY - gesture.y;
    const dx = event.clientX - gesture.x;
    // A swipe must not also activate the button's following synthetic click.
    suppressHandleClick.current = Math.max(Math.abs(dx), Math.abs(dy)) > 8;
    if (Math.abs(dy) < 32 || Math.abs(dy) <= Math.abs(dx) || gesture.minimized !== minimized) return;
    if ((!minimized && dy > 0) || (minimized && dy < 0)) onToggleMinimized?.();
  };
  const cancelSheetGesture = () => {
    sheetGesture.current = null;
    suppressHandleClick.current = true;
  };
  const routeHome = pickupName || trip.route?.fromName || '';
  const etaLine = eta !== null ? etaHeadline(t, eta) : null;
  const arrive = eta !== null ? T(t, 'arriveAround', { time: arrivalClock(nowMs, eta, lang) }) : null;
  const startedMs = timestampMs(trip.startedAt);
  const tripTime = startedMs ? T(t, 'tripTime', { time: clockDuration(nowMs - startedMs) }) : null;
  const lastUpdate = freshness === 'stale' && lastUpdateMin !== null ? T(t, 'lastUpdate', { min: lastUpdateMin }) : null;
  const searchedFor = timestampMs(trip.createdAt) ? clockDuration(nowMs - timestampMs(trip.createdAt)) : null;

  // The one line the sheet shows, and the line under it, for each part of the trip.
  let title = '';
  let sub = null;
  let big = false;
  if (phase === 'finding') {
    title = T(t, 'findingTitle');
    sub = searchedFor ? T(t, 'findingFor', { time: searchedFor }) : null;
  } else if (phase === 'to_pickup') {
    title = T(t, 'onTheWayTitle');
    sub = eta !== null ? T(t, 'onTheWayEta', { min: Math.max(1, Math.round(eta)) }) : T(t, 'onTheWayBody');
  } else if (phase === 'at_pickup') {
    title = T(t, 'arrivedTitle');
    sub = T(t, 'arrivedBody');
  } else if (phase === 'on_trip') {
    big = true;
    title = etaLine || (freshness === 'stale' ? T(t, 'etaUpdating') : T(t, 'headingTo', { place: destinationName }));
    sub = arrive || lastUpdate;
  } else if (phase === 'waiting') {
    title = T(t, 'waitingAt', { place: destinationName });
    sub = T(t, 'waitedFor', { n: waiting.minutesStarted });
  } else if (phase === 'returning') {
    title = T(t, 'returningTitle', { place: routeHome });
    sub = etaLine ? `${etaLine} · ${arrive}` : T(t, 'returningNote');
  }
  const chipLabel = chip ? T(t, `chip${chip.key[0].toUpperCase()}${chip.key.slice(1)}`) : '';

  return (
    <div className="itt-screen" style={sheetHeight ? { '--itt-sheet-h': `${sheetHeight}px` } : undefined}>
      {map}

      {showBack && (
        <button type="button" className="itt-back" onClick={onBack} aria-label={t('nav.home')}><IconBack size={20} /></button>
      )}

      <div className="itt-pill">
        <span className="itt-pill-route">{routeText}</span>
        {chip && <span className={`itt-chip tone-${chip.tone}`}><i aria-hidden="true" />{chipLabel}</span>}
      </div>

      <button type="button" className="itt-safety-fab" onClick={onOpenSafety} aria-label={T(t, 'safetyHelp')}>
        <IconShield size={22} />
      </button>

      {delayed && (
        <div className="itt-banner" role="status">
          <IconAlert size={20} />
          <div><b>{T(t, 'delayedTitle')}</b><span>{T(t, 'delayedBody')}</span></div>
        </div>
      )}

      {panned && <button type="button" className="itt-recenter" onClick={onRecenter} aria-label={t('intertown.recenterMap')}><MapRecenterIcon /></button>}

      <section ref={sheetRef} className={`itt-sheet${minimized ? ' is-min' : ''}`} aria-label={routeText}>
        <button
          type="button"
          className="itt-handle"
          onPointerDown={startSheetGesture}
          onPointerUp={finishSheetGesture}
          onPointerCancel={cancelSheetGesture}
          onLostPointerCapture={() => { sheetGesture.current = null; }}
          onClick={(event) => {
            if (event.detail !== 0 && suppressHandleClick.current) {
              suppressHandleClick.current = false;
              return;
            }
            onToggleMinimized?.();
          }}
          aria-label={minimized ? t('active.showDetails') : t('active.showMap')}
          aria-expanded={!minimized}
        />

        {minimized ? (
          <>
            <button type="button" className="itt-peek" onClick={onToggleMinimized}>
              <strong>{title}</strong>
              {sub && <span>{sub}</span>}
            </button>
            {phase !== 'finding' && (
              <DriverRow t={t} trip={trip} driverProfile={driverProfile} compact onCall={onCall} onShowDriver={onShowDriver} />
            )}
          </>
        ) : (
          <div className="itt-body">
            <Steps t={t} phase={phase} />
            <div className="itt-head">
              <h2 className={big ? 'is-big' : ''}>{title}</h2>
              {sub && <p className="itt-sub">{sub}</p>}
              {phase === 'on_trip' && tripTime && <p className="itt-sub is-soft">{tripTime}</p>}
            </div>

            {phase === 'at_pickup' && <PinCard t={t} otp={tripOtp} />}
            {phase === 'waiting' && (
              <>
                <WaitCard t={t} policy={trip.waitingPolicy} waiting={waiting} />
                <p className="itt-note">{T(t, 'returnNote')}</p>
              </>
            )}

            {phase !== 'finding' && (
              <DriverRow t={t} trip={trip} driverProfile={driverProfile} onCall={onCall} onShowDriver={onShowDriver} />
            )}
            {phase !== 'finding' && <Actions t={t} onShare={onShare} onOpenSafety={onOpenSafety} hint={phase === 'on_trip'} />}
            <FareRows t={t} trip={trip} phase={phase} waiting={waiting} />
            {phase === 'returning' && <p className="itt-note"><IconInfo size={15} />{T(t, 'returningNote')}</p>}
            {['on_trip', 'returning', 'to_pickup'].includes(phase) && (
              <LiveLine t={t} freshness={freshness} lastUpdateMin={lastUpdateMin} />
            )}
            {canCancel && <button type="button" className="itt-cancel" onClick={onCancel}>{T(t, 'cancelTrip')}</button>}
            {phase === 'finding' && <p className="itt-note">{T(t, 'findingBody')}</p>}
          </div>
        )}
      </section>

      {safetyOpen && (
        <SafetySheet
          t={t}
          supportAvailable={supportAvailable}
          alerting={alerting}
          alerted={alerted}
          onClose={onCloseSafety}
          onCall112={onCall112}
          onAlert={onAlert}
          onCallSupport={onCallSupport}
          onReport={onReport}
        />
      )}
    </div>
  );
}
